import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Corporate", "Council / Community", "Shopping Centre", "Private Party", "School", "Festival / Event", "Other"];
const COURT_OPTIONS = ["1 court", "2 courts", "3 courts", "4+ courts"];

const INITIAL = { name: "", email: "", phone: "", event_date: "", location: "", event_type: "", courts: "", message: "" };

const inputCls =
  "h-12 rounded-xl border-white/15 bg-carbon text-white placeholder:text-white/35 focus-visible:ring-volt focus-visible:ring-offset-0";

export default function QuoteSection() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSent(true);
      toast.success("Quote request sent — we'll reply within 1 hour.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="relative overflow-hidden py-24 lg:py-32" data-testid="quote-section">
      <div className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-volt/10 blur-[140px]" />
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt">Get a free quote</p>
          <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
            The court is set. <span className="text-volt">All you need to do is book.</span>
          </h2>
          <p className="mt-6 max-w-md text-base text-white/70 sm:text-lg">
            Tell us your date, location and what you're planning. We'll send a tailored proposal within 1 hour —
            available across Melbourne and Victoria.
          </p>
          <div className="mt-10 space-y-4">
            <a
              href="mailto:popuppickles@gmail.com"
              data-testid="quote-contact-email"
              className="flex items-center gap-3 text-white/80 transition-colors hover:text-volt"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-volt/10 text-volt">
                <Mail size={18} />
              </span>
              popuppickles@gmail.com
            </a>
            <a
              href="tel:0434879138"
              data-testid="quote-contact-phone"
              className="flex items-center gap-3 text-white/80 transition-colors hover:text-volt"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-volt/10 text-volt">
                <Phone size={18} />
              </span>
              0434 879 138
            </a>
            <div className="flex items-center gap-3 text-white/80">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-volt/10 text-volt">
                <MapPin size={18} />
              </span>
              Melbourne Metro · Regional VIC by appointment
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-white/10 bg-panel p-7 sm:p-9" data-testid="quote-form-panel">
            {sent ? (
              <div className="flex flex-col items-center py-14 text-center" data-testid="quote-form-success-banner">
                <CheckCircle2 size={52} className="text-volt" />
                <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">Request received</h3>
                <p className="mt-3 max-w-sm text-sm text-white/60 sm:text-base">
                  Thanks {form.name.split(" ")[0] || "there"} — your quote request is in. We'll get back to you within
                  1 hour with a tailored proposal.
                </p>
                <button
                  data-testid="quote-form-send-another"
                  onClick={() => {
                    setForm(INITIAL);
                    setSent(false);
                  }}
                  className="mt-8 rounded-full border border-white/25 px-6 py-3 font-display text-base font-bold uppercase text-white transition-colors hover:border-volt hover:text-volt"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input required placeholder="Your name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="quote-form-name-input" />
                  <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-form-email-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="quote-form-phone-input" />
                  <Input type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} data-testid="quote-form-date-input" />
                </div>
                <Input placeholder="Event location / venue" value={form.location} onChange={set("location")} className={inputCls} data-testid="quote-form-location-input" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select value={form.event_type} onValueChange={set("event_type")}>
                    <SelectTrigger className={inputCls} data-testid="quote-form-event-type-select">
                      <SelectValue placeholder="Event type" />
                    </SelectTrigger>
                    <SelectContent className="border-white/15 bg-panel text-white">
                      {EVENT_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`quote-form-event-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={form.courts} onValueChange={set("courts")}>
                    <SelectTrigger className={inputCls} data-testid="quote-form-courts-select">
                      <SelectValue placeholder="How many courts?" />
                    </SelectTrigger>
                    <SelectContent className="border-white/15 bg-panel text-white">
                      {COURT_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`quote-form-courts-${c.replace(/[^a-z0-9]+/g, "-")}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Tell us about your event — date flexibility, crowd size, add-ons you're eyeing..."
                  value={form.message}
                  onChange={set("message")}
                  className="min-h-[120px] rounded-xl border-white/15 bg-carbon text-white placeholder:text-white/35 focus-visible:ring-volt focus-visible:ring-offset-0"
                  data-testid="quote-form-message-input"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-form-submit-button"
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-volt py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-volt-hover disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Get my free quote
                      <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-white/40">Free quote · 1hr turnaround · No lock-in</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
